import { useCallback, useEffect, useRef, useState } from 'react'
import {
  deleteBlockApi,
  ensureThread,
  fetchDraft,
  patchBlock,
  VersionConflictError,
  type DraftBody,
} from '../../components/proposal-editor/api'
import type { Block } from '../../components/proposal-editor/blocks/types'

const UNDO_MAX = 20

interface UseDocSyncResult {
  draft: DraftBody | null
  threadId: string | null
  loading: boolean
  error: string | null
  updateBlock: (block: Block) => Promise<void>
  deleteBlock: (blockId: string) => Promise<void>
  refetch: () => Promise<void>
  reset: () => void
  undo: () => void
  canUndo: boolean
}

function applyBlock(blocks: Block[], block: Block): Block[] {
  return blocks.map((b) => (b.id === block.id ? block : b))
}

export function useDocSync(caseId: string, refetchSignal?: string): UseDocSyncResult {
  const [draft, setDraft] = useState<DraftBody | null>(null)
  const [threadId, setThreadId] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const undoStack = useRef<DraftBody[]>([])
  const mounted = useRef(true)

  useEffect(
    () => () => {
      mounted.current = false
    },
    [],
  )

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const r = await ensureThread(caseId)
      if (!mounted.current) return
      setThreadId(r.thread_id)
      setDraft(r.draft)
      setError(null)
    } catch (e) {
      if (mounted.current) setError(e instanceof Error ? e.message : String(e))
    } finally {
      if (mounted.current) setLoading(false)
    }
  }, [caseId])

  useEffect(() => {
    void load()
  }, [load])

  const refetch = useCallback(async () => {
    try {
      const next = await fetchDraft(caseId)
      if (mounted.current) setDraft(next)
    } catch (e) {
      if (mounted.current) setError(e instanceof Error ? e.message : String(e))
    }
  }, [caseId])

  const lastSig = useRef<string | undefined>(refetchSignal)
  useEffect(() => {
    if (refetchSignal !== undefined && refetchSignal !== lastSig.current) {
      lastSig.current = refetchSignal
      void refetch()
    }
  }, [refetchSignal, refetch])

  const pushUndo = useCallback((snapshot: DraftBody) => {
    undoStack.current.push(snapshot)
    if (undoStack.current.length > UNDO_MAX) undoStack.current.shift()
  }, [])

  const updateBlock = useCallback(
    async (block: Block) => {
      if (!draft) return
      const previous = draft
      pushUndo(previous)
      const optimistic: DraftBody = {
        ...draft,
        blocks: applyBlock(draft.blocks, block),
      }
      setDraft(optimistic)

      try {
        const newVersion = await patchBlock(caseId, block, draft.version)
        if (mounted.current) setDraft({ ...optimistic, version: newVersion })
      } catch (e) {
        if (e instanceof VersionConflictError) {
          const fresh = await fetchDraft(caseId)
          try {
            const v = await patchBlock(caseId, block, fresh.version)
            if (mounted.current)
              setDraft({ ...fresh, blocks: applyBlock(fresh.blocks, block), version: v })
            return
          } catch (retryErr) {
            if (mounted.current) setDraft(fresh)
            throw retryErr
          }
        }
        if (mounted.current) setDraft(previous)
        throw e
      }
    },
    [caseId, draft, pushUndo],
  )

  const deleteBlock = useCallback(
    async (blockId: string) => {
      if (!draft) return
      const previous = draft
      pushUndo(previous)
      const optimistic: DraftBody = {
        ...draft,
        blocks: draft.blocks.filter((b) => b.id !== blockId),
      }
      setDraft(optimistic)
      try {
        const newVersion = await deleteBlockApi(caseId, blockId, draft.version)
        if (mounted.current) setDraft({ ...optimistic, version: newVersion })
      } catch (e) {
        if (mounted.current) setDraft(previous)
        throw e
      }
    },
    [caseId, draft, pushUndo],
  )

  const undo = useCallback(() => {
    const prev = undoStack.current.pop()
    if (prev && mounted.current) setDraft(prev)
  }, [])

  return {
    draft,
    threadId,
    loading,
    error,
    updateBlock,
    deleteBlock,
    refetch,
    reset: () => {
      undoStack.current = []
    },
    undo,
    canUndo: undoStack.current.length > 0,
  }
}
